import React, { useState, useMemo } from 'react'
import { useHRStore } from '../store/hrStore'
import { Calendar, Clock, CheckCircle, AlertCircle, Users, Plus, Trash2 } from 'lucide-react'

export default function LeaveManagementPage() {
  const { employees, leaveRequests, addLeaveRequest, approveLeave, rejectLeave } = useHRStore()
  const [showAddModal, setShowAddModal] = useState(false)
  const [leaveForm, setLeaveForm] = useState({
    employeeId: '',
    type: 'Vacation',
    startDate: '',
    endDate: '',
    days: 0,
    reason: '',
  })

  const pendingLeaves = useMemo(() => {
    return leaveRequests.filter((l) => l.status === 'Pending')
  }, [leaveRequests])

  const approvedLeaves = useMemo(() => {
    return leaveRequests.filter((l) => l.status === 'Approved')
  }, [leaveRequests])

  const calculateDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 0
    const start = new Date(startDate)
    const end = new Date(endDate)
    return Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1
  }

  const handleSaveLeave = () => {
    if (!leaveForm.employeeId || !leaveForm.startDate || !leaveForm.endDate) {
      alert('Please fill in all required fields')
      return
    }

    const emp = employees.find((e) => e.id === parseInt(leaveForm.employeeId))
    const days = calculateDays(leaveForm.startDate, leaveForm.endDate)

    addLeaveRequest({
      employeeId: parseInt(leaveForm.employeeId),
      employeeName: emp?.name || 'Unknown',
      type: leaveForm.type,
      startDate: leaveForm.startDate,
      endDate: leaveForm.endDate,
      days: days,
      reason: leaveForm.reason,
    })

    setShowAddModal(false)
    setLeaveForm({
      employeeId: '',
      type: 'Vacation',
      startDate: '',
      endDate: '',
      days: 0,
      reason: '',
    })
  }

  const getTypeColor = (type) => {
    switch (type) {
      case 'Vacation':
        return 'bg-blue-500/20 text-blue-300'
      case 'Sick Leave':
        return 'bg-red-500/20 text-red-300'
      case 'Personal':
        return 'bg-yellow-500/20 text-yellow-300'
      case 'Maternity':
        return 'bg-pink-500/20 text-pink-300'
      default:
        return 'bg-slate-500/20 text-slate-300'
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved':
        return 'bg-green-500/20 text-green-300'
      case 'Pending':
        return 'bg-yellow-500/20 text-yellow-300'
      case 'Rejected':
        return 'bg-red-500/20 text-red-300'
      default:
        return 'bg-slate-500/20 text-slate-300'
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-6">
      <div className="space-y-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Leave & Absence Management</h1>
          <p className="text-slate-400">Manage vacation, sick days, and absence requests with approval workflows</p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-yellow-500/20 p-6 hover:border-yellow-400/40 transition-all">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-slate-400 text-sm font-medium">Pending Requests</h3>
              <AlertCircle className="w-5 h-5 text-yellow-400" />
            </div>
            <div className="text-3xl font-bold text-white">{pendingLeaves.length}</div>
            <div className="text-sm text-slate-400">Awaiting approval</div>
          </div>

          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-green-500/20 p-6 hover:border-green-400/40 transition-all">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-slate-400 text-sm font-medium">Approved Leaves</h3>
              <CheckCircle className="w-5 h-5 text-green-400" />
            </div>
            <div className="text-3xl font-bold text-white">{approvedLeaves.length}</div>
            <div className="text-sm text-slate-400">Upcoming leaves</div>
          </div>

          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-slate-400 text-sm font-medium">Total Requests</h3>
              <Calendar className="w-5 h-5 text-blue-400" />
            </div>
            <div className="text-3xl font-bold text-white">{leaveRequests.length}</div>
            <div className="text-sm text-slate-400">All time</div>
          </div>

          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-cyan-500/20 p-6 hover:border-cyan-400/40 transition-all">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-slate-400 text-sm font-medium">Leave Days Used</h3>
              <Clock className="w-5 h-5 text-cyan-400" />
            </div>
            <div className="text-3xl font-bold text-white">{leaveRequests.reduce((sum, l) => sum + (l.status === 'Approved' ? l.days : 0), 0)}</div>
            <div className="text-sm text-slate-400">Approved days</div>
          </div>
        </div>

        {/* Pending Requests */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-yellow-500/20 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <AlertCircle className="w-6 h-6 text-yellow-400" />
              Pending Requests ({pendingLeaves.length})
            </h2>
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-all"
            >
              <Plus className="w-4 h-4" />
              Request Leave
            </button>
          </div>

          {pendingLeaves.length > 0 ? (
            <div className="space-y-3">
              {pendingLeaves.map((leave) => (
                <div key={leave.id} className="bg-slate-700/50 rounded-lg p-4 border border-yellow-500/20">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="text-white font-semibold">{leave.employeeName}</h3>
                      <p className="text-sm text-slate-400">{leave.reason}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${getTypeColor(leave.type)}`}>
                      {leave.type}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm mb-3">
                    <span className="text-blue-300">
                      {leave.startDate} to {leave.endDate}
                    </span>
                    <span className="text-cyan-400 font-semibold">{leave.days} days</span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => approveLeave(leave.id)}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm transition-all"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => rejectLeave(leave.id)}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-all"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">No pending requests</div>
          )}
        </div>

        {/* Approved Leaves */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-green-500/20 p-6">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <CheckCircle className="w-6 h-6 text-green-400" />
            Approved Leaves
          </h2>

          {approvedLeaves.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-blue-500/20">
                    <th className="text-left py-3 px-4 text-blue-300 font-semibold">Employee</th>
                    <th className="text-left py-3 px-4 text-blue-300 font-semibold">Type</th>
                    <th className="text-left py-3 px-4 text-blue-300 font-semibold">Dates</th>
                    <th className="text-center py-3 px-4 text-blue-300 font-semibold">Days</th>
                    <th className="text-left py-3 px-4 text-blue-300 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {approvedLeaves.map((leave) => (
                    <tr key={leave.id} className="border-b border-blue-500/10 hover:bg-slate-700/30 transition-colors">
                      <td className="py-3 px-4 text-white font-semibold">{leave.employeeName}</td>
                      <td className="py-3 px-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${getTypeColor(leave.type)}`}>
                          {leave.type}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-blue-300">
                        {leave.startDate} to {leave.endDate}
                      </td>
                      <td className="py-3 px-4 text-center text-cyan-400 font-semibold">{leave.days}</td>
                      <td className="py-3 px-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(leave.status)}`}>
                          {leave.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">No approved leaves</div>
          )}
        </div>

        {/* Add Leave Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-xl border border-yellow-500/20 max-w-md w-full mx-4 p-6">
              <h2 className="text-2xl font-bold text-white mb-4">Request Leave</h2>

              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-slate-400 text-sm mb-2">Employee</label>
                  <select
                    value={leaveForm.employeeId}
                    onChange={(e) => setLeaveForm({ ...leaveForm, employeeId: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-yellow-500 focus:outline-none"
                  >
                    <option value="">Select Employee</option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-slate-400 text-sm mb-2">Leave Type</label>
                  <select
                    value={leaveForm.type}
                    onChange={(e) => setLeaveForm({ ...leaveForm, type: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-yellow-500 focus:outline-none"
                  >
                    <option value="Vacation">Vacation</option>
                    <option value="Sick Leave">Sick Leave</option>
                    <option value="Personal">Personal</option>
                    <option value="Maternity">Maternity</option>
                  </select>
                </div>

                <div>
                  <label className="block text-slate-400 text-sm mb-2">Start Date</label>
                  <input
                    type="date"
                    value={leaveForm.startDate}
                    onChange={(e) => setLeaveForm({ ...leaveForm, startDate: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-yellow-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-slate-400 text-sm mb-2">End Date</label>
                  <input
                    type="date"
                    value={leaveForm.endDate}
                    onChange={(e) => setLeaveForm({ ...leaveForm, endDate: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-yellow-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-slate-400 text-sm mb-2">Reason</label>
                  <textarea
                    value={leaveForm.reason}
                    onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-yellow-500 focus:outline-none"
                    placeholder="Reason for leave"
                    rows="3"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveLeave}
                  className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg transition-all"
                >
                  Request
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
